import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout.js';
import Section from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';
import { FaHome, FaMapMarkerAlt } from 'react-icons/fa';

// Hero section for the ForRent page
const Hero = styled.div`
  position: relative;
  background-color: var(--primary);
  color: white;
  padding: 4rem 0;
  margin-bottom: 3rem;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/images/hero2.png');
    background-size: cover;
    background-position: center;
    opacity: 0.3;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }
  
  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    
    h1 {
      font-size: 2.25rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Empty state message styling
const EmptyState = styled.div`
  background-color: var(--light);
  border-radius: var(--border-radius-lg);
  padding: 3rem;
  text-align: center;
  max-width: 800px;
  margin: 2rem auto 4rem;
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
    font-size: 1.75rem;
  }
  
  p {
    color: var(--dark);
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
  
  svg {
    font-size: 3rem;
    color: var(--secondary);
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem auto 3rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Future listing grid
const ListingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Placeholder listing card
const ListingCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-5px);
  }
  
  .image {
    width: 100%;
    height: 200px;
    background-color: var(--gray);
    position: relative;
    
    .status {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background-color: var(--secondary);
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: var(--border-radius-sm);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
    }
  }
  
  .details {
    padding: 1.5rem;
    
    h3 {
      color: var(--primary);
      margin-bottom: 0.5rem;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      
      svg {
        margin-right: 0.5rem;
        color: var(--secondary);
      }
    }
    
    p {
      color: var(--dark);
      margin-bottom: 1rem;
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }
`;

// Title section for the future listings
const ListingsSectionTitle = styled.div`
  text-align: center;
  margin: 4rem 0 2rem;
  
  h2 {
    color: var(--primary);
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--gray);
    max-width: 600px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    margin: 3rem 0 1.5rem;
    
    h2 {
      font-size: 1.75rem;
    }
  }
`;

const ForRentPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <Hero>
        <div className="container">
          <h1>Rental Properties</h1>
          <p>Explore Shoffner rental homes — well-maintained, locally built, and available across the NC Triad.</p>
        </div>
      </Hero>
      
      <Section>
        <div className="container">
          {/* Empty State Message */}
          <EmptyState>
            <FaHome />
            <h3>Rental Properties Coming Soon</h3>
            <p>We currently don't have any rental properties available, but check back soon!</p>
            <Button as={Link} to="/contact" variant="secondary">Contact Us for Updates</Button>
          </EmptyState>
          
          {/* Future Listings Section */}
          <ListingsSectionTitle>
            <h2>Future Rentals</h2>
            <p>This section will showcase our upcoming rental properties</p>
          </ListingsSectionTitle>
          
          {/* Placeholder Grid for Future Listings */}
          <ListingsGrid>
            {/* Example Placeholder Card 1 */}
            <ListingCard>
              <div className="image">
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Sample Rental Location</h3>
                <p>This is where the description of the rental property will appear. Details about the home, its features, and specifications will be listed here.</p>
                <Button as="div" variant="outline" fullWidth disabled>View Details</Button>
              </div>
            </ListingCard>
            
            {/* Example Placeholder Card 2 */}
            <ListingCard>
              <div className="image">
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Sample Rental Location</h3>
                <p>This is where the description of the rental property will appear. Details about the home, its features, and specifications will be listed here.</p>
                <Button as="div" variant="outline" fullWidth disabled>View Details</Button>
              </div>
            </ListingCard>
            
            {/* Example Placeholder Card 3 */}
            <ListingCard>
              <div className="image">
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Sample Rental Location</h3>
                <p>This is where the description of the rental property will appear. Details about the home, its features, and specifications will be listed here.</p>
                <Button as="div" variant="outline" fullWidth disabled>View Details</Button>
              </div>
            </ListingCard>
          </ListingsGrid>
        </div>
      </Section>
    </Layout>
  );
};

export default ForRentPage;